import { useState, useMemo, useEffect, useRef } from "react";
import axios from "axios";
import { useQuery } from "@tanstack/react-query";
import { Card, CardContent } from "./ui/card";
import { Button } from "./ui/button";
import {
    Star,
    Phone,
    Video,
    BadgeCheck,
    Calendar,
} from "lucide-react";
import { connect, useSelector } from "react-redux";
import { RootState } from "../state/store";
import { CallModal } from "./ui/callmodal";
import { readingLine, usdToLocalLabel } from "../utilities/currency";
import { parseUntilObject, getAgentDisplayPicUrl } from "../utilities/conversions";
import { BookingSelector } from "./ui/bookingmodal.tsx";
import SearchField from "./ui/searchfield.tsx";



export default function Agents({
    baseUrl,
    T,
    currentUser,
    onLoginClick,
    onLoginSuccess,
    searchField,
    setSearchField,
    onSearchClick,
    filteredAdvisors,
}: {
    baseUrl: string;
    T: any;
    currentUser: any;
    onLoginClick: () => void;
    onLoginSuccess: () => void;
    searchField: string;
    setSearchField: (e: string) => void,
    onSearchClick: () => void,
    filteredAdvisors: any,
}) {

    const [showBooking, setShowBooking] = useState(false);
    const [selectedAgentToPass, setSelectedAgentToPass] = useState(null); // 👈 store agent here
    const client = useSelector((state: RootState) => state.client.client);
    
    const fetchCustomer = async () => {
        const response = await axios.get(baseUrl + "/getCustomerByUID", {
            params: { token: currentUser.token },
        });
        if (response.data && response.data.status === "OK") {
            return response.data.data.uid;
        }
        throw new Error("Failed to fetch customer");
    };
    const { data: customer_uid } = useQuery({
        queryKey: ["customer", currentUser?.token],
        queryFn: fetchCustomer,
        enabled: !!currentUser, // only run if logged in
    });

    // 🟢 2. Store latest customer_uid in a ref so WebSocket always uses current one
    const customerUidRef = useRef(customer_uid);

    useEffect(() => {
        customerUidRef.current = customer_uid;
    }, [customer_uid]);
    const default_configs = client ? JSON.parse(client.default_configs) : "";
    const footer_settings = client ? JSON.parse(client.footer_settings) : "";

    const [selectedAgent, setSelectedAgent] = useState<string | null>(null);
    const [minutePrice, setMinutePrice] = useState<string | null>(null);
    const [callStartupFee, setCallStartupFee] = useState<string | null>(null);
    const [showCallModal, setShowCallModal] = useState(false);
    const [webSocketConn, setWebSocketConn] = useState<WebSocket | null>(null);
    const webSocketRef = useRef<WebSocket | null>(null);

    const [type, setType] = useState<string>("audio");

    const [vippsAuthenticate, setVippsAuthenticate] = useState(false);
    const [walletAuthenticate, setWalletAuthenticate] = useState(false);
    const [stripeAuthenticate, setStripeAuthenticate] = useState(false);
    const [responseAgent, setResponseAgent] = useState(false);
    const [waitingAgentCheck, setWaitingAgentCheck] = useState(false);
    const [callResponseObj, setCallResponseObj] = useState<any>(null);
    const [waitingCallback, setWaitingCallback] = useState(false);
    const [waitingCallbackInfo, setWaitingCallbackInfo] = useState<any>(null);



    //cancel convert call to callback
    const handleCancelConvertCallToCallback = (call_id: string) => {
        setShowCallModal(false);
        var formData = new FormData();
        formData.append('call_id', call_id);
        axios.post(baseUrl + "/cancelWebCallPayment", formData);
    }

    //convert call to callback
    const handleConvertCallToCallback = async (call_id: string) => {
        var formData = new FormData();
        formData.append('call_id', call_id);
        const response = await axios.post(baseUrl + "/updateWebCallToCallback", formData);

        if (response.data && response.data.status === "OK") {
            var data = {
                call_history_id: call_id,
                about: "callback_initated_call",
                channel: "common_channel",
                type: 'audio_call',
            };
            const connection = webSocketConn;
            if (connection && connection.readyState === WebSocket.OPEN) {
                connection.send(JSON.stringify(data));
            }

            setWaitingCallback(true);
            setWaitingCallbackInfo({ send_callback_email: response.data.send_callback_email });
            setCallResponseObj(null);
            setResponseAgent(false);
        }
    }


    function statusPill(status: string) {
        status = status?.toUpperCase();
        if (status === "OFFLINE") return "AVLOGGET";
        if (status === "BUSY") return "OPPTATT";
        if (status === "AVAILABLE") return "LEDIG";
        return status;
    }

    const reconnectTimeout = useRef<number | null>(null);

    const connectWebSocket = () => {
        try {
            const wsServer = baseUrl.includes("localhost")
                ? "ws://localhost:8013/echo"
                : "wss://chamcore.com/echo";

            const connection = new WebSocket(wsServer);

            connection.onopen = () => {
                console.log("✅ WebSocket connected");
                connection.send("Connection Established!");
            };

            connection.onmessage = (e) => {
                if (!e.data) return;

                const data = parseUntilObject(e.data);
                if (!data || !data.information) return;

                const latestCustomerUid = customerUidRef.current;

                // --- your logic ---
                if (data.type === "video_call") {
                    if (data.information.customer_id === latestCustomerUid && data.about === "rejected_call") {
                        setResponseAgent(true);
                        setWaitingAgentCheck(false);
                        setCallResponseObj({ title: "Anrop avvist av agent", message: "Samtalen avbrutt" });
                    } else if (data.information.customer_id === latestCustomerUid && data.about === "accepted_call") {
                        setWaitingAgentCheck(false);
                        setResponseAgent(false);
                        setCallResponseObj(null);
                        setShowCallModal(false);
                        window.open("/customer/one-to-one-video-call/" + data.call_history_id, "_blank");
                    } else if (data.information.customer_id === latestCustomerUid && data.about === "timeout_by_agent") {
                        setResponseAgent(true);
                        setWaitingAgentCheck(false);
                        setCallResponseObj({ title: "Tidsavbrudd", message: "Agenten tok ikke samtalen i tide." });
                        let formData = new FormData();
                        formData.append("call_id", data.call_history_id);
                        formData.append("token", currentUser.token);
                        formData.append("call_status", "missed");
                        axios.post(baseUrl + "/hangupCall", formData);
                    }
                } else if (data.type === "audio_call") {
                    if (data.information.customer_id === latestCustomerUid && data.about === "WebCall_rejected_by_agent") {
                        setResponseAgent(true);
                        setWaitingAgentCheck(false);
                        setCallResponseObj({
                            title: "Beklager at agenten avviste anropet ditt.",
                            message: "Av en eller annen grunn kunne ikke agenten ta imot samtalen din akkurat nå.",
                            more_info: { call_history_id: data.call_history_id },
                        });
                    } else if (data.information.customer_id === latestCustomerUid && data.about === "WebCall_accepted_call") {
                        setResponseAgent(false);
                        setWaitingAgentCheck(false);
                        setCallResponseObj(null);
                        setVippsAuthenticate(false);
                        setWalletAuthenticate(false);
                        setStripeAuthenticate(false);
                        setShowCallModal(false);
                        setTimeout(() => {
                            window.open("/customer/one-to-one-audio-call/" + data.call_history_id, "_blank");
                        }, 5000);
                    } else if (data.information.customer_id === latestCustomerUid && data.about === "WebCall_timeout_by_agent") {
                        setResponseAgent(true);
                        setWaitingAgentCheck(false);
                        setCallResponseObj({ title: "Tidsavbrudd", message: "Agenten tok ikke samtalen i tide." });
                        let formData = new FormData();
                        formData.append("call_id", data.call_history_id);
                        formData.append("token", currentUser.token);
                        formData.append("call_status", "missed");
                        axios.post(baseUrl + "/hangupCall", formData);
                    }
                }
            };

            connection.onclose = () => {
                console.log("⚠️ WebSocket closed, retrying in 2s...");
                reconnectTimeout.current = setTimeout(connectWebSocket, 100);
            };

            connection.onerror = (err) => {
                console.log("❌ WebSocket error:", err);
                connection.close();
            };

            // store the connection
            webSocketRef.current = connection;
            setWebSocketConn(connection);
        } catch (err) {
            console.error("WebSocket setup failed:", err);
        }
    };

    // initial connect
    useEffect(() => {
        connectWebSocket();
        return () => {
            if (webSocketConn) webSocketConn.close();
            if (reconnectTimeout.current) clearTimeout(reconnectTimeout.current);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [baseUrl]);

    // Reconnect on tab focus
    // useEffect(() => {
    //     const handleVisibilityChange = () => {
    //         if (!document.hidden) {
    //             console.log("🟢 Tab active — checking WebSocket...");
    //             if(webSocketConn){
    //                 webSocketConn.close();
    //             }
    //             if (!webSocketConn || webSocketConn.readyState !== WebSocket.OPEN) {
    //                 connectWebSocket();
    //             }
    //         }
    //     };

    //     document.addEventListener("visibilitychange", handleVisibilityChange);
    //     return () => document.removeEventListener("visibilitychange", handleVisibilityChange);
    // }, [webSocketConn]);

    const handlePhoneClick = (
        uid: string,
        minute_price: number,
        call_startup_fee: string,
        agent_call_permission: string,
        type: string,
    ) => {
        if (default_configs.WebRTC === "yes" && agent_call_permission === "1") {
            setSelectedAgent(uid);
            setMinutePrice(minute_price.toString());
            setCallStartupFee(call_startup_fee);
            setShowCallModal(true);
            setType(type);
            setResponseAgent(false);
        } else {
            window.location.href = `tel:${footer_settings.footer_phone}`;
        }
    };

    const handlePhoneBillClick = (phonebill: string) => {
        window.location.href = `tel:${phonebill}`;
    }

    const handleBookingClick = (agent: any) => {
        // set prop agent in BookingSelector
        setSelectedAgentToPass(agent);
        setShowBooking(true);
    }
    const purpose = "agent_search";
    const placeholder = "Skriv inn navn for å søke";
    

    return (
        <section id="agents" className="py-12 px-4 max-w-7xl mx-auto">
            <SearchField purpose={purpose} placeholder={placeholder} onSearchClick={onSearchClick} searchField={searchField} setSearchField={setSearchField} />
            <div className="grid gap-6 md:grid-cols-4">
                {filteredAdvisors.length < 1 && (<div>Ikke funnet</div>)}
                {filteredAdvisors.slice(0, 4).map((a: any, index: number) => (
                    <Card
                        key={`a-top-${index}`}
                        className="rounded-2xl shadow-md hover:shadow-lg transition overflow-hidden relative bg-gradient-to-b from-purple-50 to-white"
                    >
                        <a href={"agent/" + a.code}>
                            <img
                                src={a.display_pic ?? ""}
                                alt={a.name ?? ""}
                                className="w-full h-64 object-contain"
                            />
                        </a>
                        {/* <div className="absolute top-3 right-3 bg-white rounded-full p-1 shadow">
                            <Play className="w-5 h-5 text-purple-700" />
                        </div> */}
                        <CardContent className="p-4">
                            <div className="flex justify-between items-center">
                                <h3 className="text-lg font-bold text-purple-700 flex items-center gap-2">
                                    <a href={"agent/" + a.code}>{a.name}</a>
                                    {a.is_active && (
                                        <span className="inline-flex items-center gap-1 text-green-700 text-xs font-semibold bg-green-100 px-2 py-0.5 rounded-full">
                                            <BadgeCheck className="w-3 h-3" /> {T.verified}
                                        </span>
                                    )}
                                </h3>
                                <div className="flex items-center gap-1 text-yellow-500 text-sm">
                                    <Star className="w-4 h-4" /> {a.rating}
                                </div>
                            </div>
                            <p className="text-gray-600 italic text-sm">{a.skills}</p>
                            {/* <div className="mt-2 text-sm font-semibold">
                                {readingLine(Number(a.day_month ?? 0), Number(a.created_year ?? 0))}
                            </div> */}
                            <span
                                className={`inline-block mt-2 px-3 py-1 text-xs font-bold rounded-full ${a.call_status?.toUpperCase() === "AVAILABLE" ||
                                    a.call_status?.toUpperCase() === "ONLINE"
                                    ? "bg-green-500 text-white"
                                    : a.call_status?.toUpperCase() === "OFFLINE" ? "bg-gray-200 text-black" : "bg-red-500 text-white"
                                    }`}
                            >
                                {statusPill(a.call_status)}
                            </span>
                            <div className="flex gap-4 mt-3 text-purple-600">
                                {a?.permissions?.booking === "1" ? (
                                    <Calendar className="w-5 h-5 cursor-pointer" onClick={() => handleBookingClick(a)} />
                                ) : ('')}
                                <Phone
                                    className="w-5 h-5 cursor-pointer"
                                    onClick={() =>
                                        handlePhoneClick(
                                            a.uid,
                                            a.customer_minute_price,
                                            a.customer_call_startup_fee,
                                            a.permissions.WebRTC,
                                            'audio'
                                        )
                                    }
                                />
                                {default_configs.video === "yes" && a.can_video_call === "1" ? (
                                    <Video onClick={() =>
                                        handlePhoneClick(
                                            a.uid,
                                            a.customer_minute_price,
                                            a.customer_call_startup_fee,
                                            a.permissions.WebRTC,
                                            'video'
                                        )
                                    } className="w-5 h-5 cursor-pointer" />
                                ) : (
                                    ""
                                )}
                                {a.phonebill ? (
                                    <img onClick={() => handlePhoneBillClick(a.phonebill)} title={a.phonebill} className="h-[23px] cursor-pointer" height="23px" src="../../strex-logo.jpeg" alt="strex logo" />
                                ) : ('')}
                            </div>
                            <div className="mt-3 text-gray-700 text-sm flex items-center gap-2">
                                {T.from} {usdToLocalLabel(String(a.customer_minute_price))}{" "}
                                <BadgeCheck className="w-4 h-4 text-green-600" />
                            </div>
                            {/* <div className="mt-2 flex items-center gap-2">
                                <span className="text-xs font-semibold rounded-full px-2 py-1 bg-purple-200/90 text-purple-900 flex items-center gap-1">
                                    <WalletIcon className="w-3 h-3" /> Vipps MobilePay
                                </span>
                                <span className="text-xs font-semibold rounded-full px-2 py-1 bg-blue-200/90 text-blue-900 flex items-center gap-1">
                                    <CreditCard className="w-3 h-3" /> Kort
                                </span>
                            </div> */}
                            <Button className="mt-4 w-full bg-purple-600 hover:bg-purple-700 text-white rounded-full">
                                {"Innvalg " + a.code}
                            </Button>
                        </CardContent>
                    </Card>
                ))}
            </div>

            <CallModal
                show={showCallModal}
                onClose={() => setShowCallModal(false)}
                baseUrl={baseUrl}
                agent_uid={selectedAgent}
                minutePrice={minutePrice}
                currentUser={currentUser}
                callStartupFee={callStartupFee}
                webSocketConn={webSocketConn}
                setWebSocketConn={setWebSocketConn}
                webSocketRef={webSocketRef}
                responseAgent={responseAgent}
                setResponseAgent={setResponseAgent}
                waitingAgentCheck={waitingAgentCheck}
                setWaitingAgentCheck={setWaitingAgentCheck}
                vippsAuthenticate={vippsAuthenticate}
                setVippsAuthenticate={setVippsAuthenticate}
                walletAuthenticate={walletAuthenticate}
                setWalletAuthenticate={setWalletAuthenticate}
                stripeAuthenticate={stripeAuthenticate}
                setStripeAuthenticate={setStripeAuthenticate}
                callResponseObj={callResponseObj}
                setCallResponseObj={setCallResponseObj}
                handleCancelConvertCallToCallback={handleCancelConvertCallToCallback}
                handleConvertCallToCallback={handleConvertCallToCallback}
                waitingCallback={waitingCallback}
                waitingCallbackInfo={waitingCallbackInfo}
                onLoginSuccess={onLoginSuccess}
                type={type}
            />
            {showBooking && <BookingSelector agent={selectedAgentToPass} onClose={() => setShowBooking(false)} baseUrl={baseUrl} client={client} currentUser={currentUser} onLoginSuccess={onLoginSuccess} />}
        </section>
    );
}
